/**
 * Scroll Reveal — ScrollTrigger entrance animations.
 * Queries [data-reveal] elements and fades them in from below.
 * Respects prefers-reduced-motion.
 *
 * ── WHY ANYTHING ALREADY VISIBLE IS NOT ANIMATED ────────────────────────────
 *
 * The tween is `once: true`, so a trigger that fires and then fails to finish
 * NEVER recovers — the element is left frozen at whatever progress it reached.
 * That is not theoretical: requestAnimationFrame is throttled in a background
 * tab, so opening the site in one (cmd-click, "open link in new tab", a restored
 * session) starts the tween, stalls the ticker, and hands the visitor a hero
 * stuck at opacity 0.25 when they switch to it. Found at exactly that value on
 * the home page, 26 Aug 2026.
 *
 * Two guards, and both are also just better behaviour:
 *
 *   1. `document.hidden` — never start an entrance animation nobody is looking
 *      at. It cannot be seen, and it is the case that freezes.
 *   2. Already within the trigger window at init — an element above the fold is
 *      not "revealed on scroll", it is simply on screen. Fading in the largest
 *      element on the page also delays LCP for no design benefit.
 *
 * Both resolve to `gsap.set(...)` final state rather than an early return, so
 * the element is explicitly put right instead of inheriting whatever the last
 * frame left behind.
 *
 * ── THE IDENTITY TRANSFORM HAS TO BE CLEARED, NOT LEFT AT ZERO ──────────────
 *
 * GSAP writes `transform: translate(0px, 0px)` inline when this finishes, and an
 * identity transform is NOT a no-op: any transform on an element makes it the
 * containing block for `position: fixed` descendants. Every block on the site is
 * wrapped in a `[data-reveal]` div, so that one leftover declaration silently
 * broke `position: fixed` for the entire page body.
 *
 * Found via ScrollTrigger `pin: true` on the case-study scroller, which pins by
 * fixing the element: instead of sticking to the viewport it anchored to the
 * reveal wrapper and sat 451px above the fold. The pin looked broken; the pin was
 * fine. Anything fixed inside a block — a pinned section, a sticky-ish overlay, a
 * dialog — would have hit the same wall, and the symptom points at the wrong file
 * every time.
 *
 * `clearProps: 'transform'` removes the declaration once the tween no longer
 * needs it. The element is already at its natural position by then, so there is
 * nothing to preserve.
 */

/** Matches ScrollTrigger's `start: 'top 85%'` below — keep the two in step. */
const TRIGGER_RATIO = 0.85;

/**
 * The reveal tween each wrapper is waiting on, so the settle pass below can
 * tell "not yet reached" from "reached and stalled". Weak, so a wrapper Swup
 * swaps out takes its entry with it.
 * @type {WeakMap<Element, object>}
 */
const pending = new WeakMap();

/** The settle pass is armed once per document, not once per visit. */
let settleArmed = false;

/**
 * Put a wrapper in its final state and drop whatever was waiting on it.
 * @param {Element} el
 */
function reveal(el) {
    const tween = pending.get(el);
    if (tween) {
        tween.scrollTrigger?.kill();
        tween.kill();
        pending.delete(el);
    }
    // clearProps, not `y: 0` — see THE IDENTITY TRANSFORM note above.
    window.gsap.set(el, { opacity: 1, clearProps: 'transform' });
}

/**
 * ── THE SETTLE PASS: A WRAPPER ON SCREEN IS NEVER LEFT HIDDEN ───────────────
 *
 * "Sometimes I have to scroll on the list pages before the items fade in — they
 * should fade in at all times, no scroll needed" (Morgan, 3 Sep 2026). It could
 * not be made to happen on demand, which is the signature of a trigger that is
 * right when it is made and wrong a moment later: a start position computed
 * before a late font or image moved the page, a tween that began in a tab the
 * reader was not looking at and stalled, a refresh ScrollTrigger only performs
 * on `load` and never on a Swup visit. Each leaves a wrapper that is plainly in
 * the viewport waiting for a scroll event to notice.
 *
 * So after every init the triggers are refreshed once the page has settled —
 * fonts ready, then a beat, then a longer beat — and every wrapper still
 * pending is checked against the viewport itself: inside the trigger line and
 * not mid-animation means the trigger has missed it, and it is revealed
 * outright. The same sweep runs whenever the tab becomes visible, which is the
 * case the docblock above records freezing a hero at opacity 0.25. Below the
 * fold nothing changes: those wrappers still reveal on scroll, as designed.
 */
function settle() {
    if (!window.gsap || !window.ScrollTrigger || document.hidden) return;

    window.ScrollTrigger.refresh();

    document.querySelectorAll('[data-reveal]').forEach((el) => {
        const tween = pending.get(el);
        if (!tween || tween.isActive()) return;

        // At or above the trigger line — on screen, or already scrolled past —
        // is "the trigger should have fired by now". A wrapper the reader has
        // scrolled past is revealed too, so scrolling back never meets a blank.
        const reached = el.getBoundingClientRect().top < window.innerHeight * TRIGGER_RATIO;

        if (reached && tween.progress() < 1) {
            reveal(el);
        }
    });
}

function armSettle() {
    if (settleArmed) return;
    settleArmed = true;

    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) settle();
    });
}

/** Refresh and sweep at the points a page's layout is likely to have moved. */
function scheduleSettle() {
    armSettle();
    document.fonts?.ready.then(settle);
    window.setTimeout(settle, 700);
    window.setTimeout(settle, 2400);
}

export function initScrollReveals() {
    if (!window.gsap || !window.ScrollTrigger) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const elements = document.querySelectorAll('[data-reveal]');

    elements.forEach((el) => {
        // Re-entry (Swup calls this on every page:view, and GSAP's own load calls
        // it once more): a wrapper already handled keeps its tween.
        if (pending.has(el)) return;

        const alreadyInWindow = el.getBoundingClientRect().top < window.innerHeight * TRIGGER_RATIO;

        if (reducedMotion || document.hidden || alreadyInWindow) {
            reveal(el);
            return;
        }

        const tween = window.gsap.fromTo(el,
            { opacity: 0, y: 40 },
            {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: 'power2.out',
                // The element is where it started; the transform has no work left
                // to do and is actively harmful if left behind.
                onComplete: () => {
                    window.gsap.set(el, { clearProps: 'transform' });
                    pending.delete(el);
                },
                scrollTrigger: {
                    trigger: el,
                    start: `top ${TRIGGER_RATIO * 100}%`,
                    once: true,
                },
            }
        );

        pending.set(el, tween);
    });

    scheduleSettle();
}
